<template>
  <div class="reco-page client-page">
    <div class="reco-page__content">
      <!-- <header class="client-page__header">
        <h1 class="client-page__title">Mes actions</h1>
        <div>

        </div>
      </header> -->
      <div class="reco-todos__toolbar">
        <div class="suivi-search-wrap">
          <MagnifyingGlassIcon class="suivi-search-icon" />
          <input
            v-model="search"
            type="search"
            class="suivi-search"
            placeholder="Rechercher un dossier…"
            autocomplete="off"
          />
        </div>
        <div class="status-filters">
          <button
            v-for="opt in statutFilters"
            :key="opt.value"
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': activeFilter === opt.value }"
            @click="setFilter(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <div v-if="todoError" class="client-alert client-alert--error reco-alert">
        <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
        {{ todoError }}
      </div>

      <div v-if="todoLoading" class="reco-loading">
        <div class="reco-loading__spinner" aria-hidden="true" />
        <p class="reco-loading__text">Chargement des actions…</p>
      </div>

      <div v-else-if="todoActions.length === 0" class="reco-todos__empty">
        Aucune action pour l’instant.
        <br>Elles apparaîtront dès que votre conseiller prendra votre dossier en charge.
      </div>

      <template v-else>
        <div class="reco-todos__progress-wrap">
          <div class="reco-card__progress-bar">
            <div class="reco-card__progress-fill" :style="{ width: todoProgress + '%' }" />
          </div>
          <span class="reco-card__progress-text">{{ todoProgress }}% — {{ todoDone.length }} / {{ todoActions.length
            }}</span>
        </div>


        <div v-if="!filteredActions.length" class="reco-todos__empty">
          Aucune action ne correspond à ce filtre.
        </div>
        <ul v-else class="reco-todos__list">
          <li
            v-for="act in filteredActions"
            :key="act.id"
            class="reco-todos__item"
            :class="{ 'reco-todos__item--late': act.en_retard, 'reco-todos__item--done': act.statut === 'terminee' }"
          >
            <label class="reco-todos__check">
              <div
                class="indicateur_todo"
                :class="{
                  'indicateur_todo--done': act.statut === 'terminee',
                  'indicateur_todo--late': act.statut !== 'terminee' && act.en_retard,
                  'indicateur_todo--pending': act.statut !== 'terminee' && !act.en_retard,
                }"
              ></div>
              <input
                type="checkbox"
                :checked="act.statut === 'terminee'"
                :disabled="todoSavingId === act.id"
                @change="toggleAction(act, $event.target.checked)"
              />
              <span class="reco-todos__desc">{{ act.description }}</span>
            </label>
            <span v-if="act.date_echeance" class="reco-todos__due">
              échéance {{ formatDate(act.date_echeance) }}
            </span>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { ExclamationCircleIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorMessages';
import { ACTIONS_STATUS_FILTERS, useActionsFilter } from '@/utils/Filter';

// Le conseiller ajoute des actions côté back-office pendant que le client peut
// avoir cette page déjà ouverte : on repolle en tâche de fond pour que les
// nouvelles actions apparaissent sans recharger. Même schéma que le polling
// clientApte de FormApat.vue (le seul précédent dans ce code, pas de WebSocket).
const ACTIONS_POLL_INTERVAL_MS = 20000;
const statutFilters = ACTIONS_STATUS_FILTERS;

const todoLoading = ref(false);
const todoError = ref('');
const todoActions = ref([]);
const todoSavingId = ref(null);
let actionsPollTimer = null;
let fetchInFlight = false;

const todoDone = computed(() => todoActions.value.filter((a) => a.statut === 'terminee'));
const todoProgress = computed(() => {
  const total = todoActions.value.length;
  if (!total) return 0;
  return Math.round((100 * todoDone.value.length) / total);
});

const { search, activeFilter, setFilter, counts, filteredActions } = useActionsFilter(() => todoActions.value);

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// `silent: true` (polling en tâche de fond, ou refetch après une action déjà
// suivie par son propre indicateur sur la case à cocher) évite de faire
// clignoter tout l'écran avec le spinner de chargement plein écran.
async function fetchTodos({ silent = false } = {}) {
  if (fetchInFlight) return;
  fetchInFlight = true;
  if (!silent) todoLoading.value = true;
  if (!silent) todoError.value = '';
  try {
    const { data } = await axios.get('/api/suivi-analyse/mes-actions/', {
      withCredentials: true,
    });
    todoActions.value = Array.isArray(data?.actions) ? data.actions : [];
  } catch (error) {
    if (error?.response?.status === 503) {
      todoActions.value = [];
      if (!silent) todoError.value = '';
      return;
    }
    if (!silent) todoError.value = getErrorMessage(error).message;
  } finally {
    fetchInFlight = false;
    if (!silent) todoLoading.value = false;
  }
}

function startActionsPolling() {
  if (actionsPollTimer) return;
  actionsPollTimer = setInterval(() => {
    fetchTodos({ silent: true });
  }, ACTIONS_POLL_INTERVAL_MS);
}

function stopActionsPolling() {
  if (actionsPollTimer) {
    clearInterval(actionsPollTimer);
    actionsPollTimer = null;
  }
}

async function toggleAction(act, done) {
  todoSavingId.value = act.id;
  try {
    await axios.patch(
      `/api/suivi-analyse/actions/${act.id}/`,
      { statut: done ? 'terminee' : 'a_faire' },
      { withCredentials: true }
    );
    await fetchTodos({ silent: true });
  } catch (error) {
    todoError.value = getErrorMessage(error).message;
  } finally {
    todoSavingId.value = null;
  }
}

onMounted(() => {
  fetchTodos();
  startActionsPolling();
});

onUnmounted(() => {
  stopActionsPolling();
});
</script>

<style scoped>
.reco-page {
  min-height: 100%;
  font-family: var(--font-body);
  color: var(--client-text);
  padding-bottom: 3rem;
}

.reco-alert {
  margin-bottom: 1.5rem;
}

.reco-loading {
  text-align: center;
  padding: 3rem 1rem;
}

.reco-loading__spinner {
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto 1rem;
  border: 3px solid var(--client-line);
  border-top-color: var(--brand-gold);
  border-radius: 50%;
  animation: reco-spin 0.8s linear infinite;
}

.reco-loading__text {
  font-size: var(--text-prose);
  color: var(--client-text-muted);
  margin: 0;
}

@keyframes reco-spin {
  to {
    transform: rotate(360deg);
  }
}

.reco-card__progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(84, 57, 41, 0.12);
  border-radius: 9999px;
  overflow: hidden;
}

.reco-card__progress-fill {
  height: 100%;
  background: var(--brand-gold);
  border-radius: 9999px;
  transition: width 0.3s ease;
}

.reco-card__progress-text {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--client-text-muted);
  min-width: 2.5rem;
}

.reco-todos__progress-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
</style>
